import { wacToken } from '@/libraries/design/foundations';

import type {
	NodeImagePlacement,
	WebNodeConfig,
	WebConnectorConfig,
	ResolvedConnector,
	ConnectorShape,
	ConnectorType,
	FillColor,
	SpiderWebCanvasClickPct,
} from './spiderweb-types';

const VIEWBOX_SCALE = 1000;

export function getFillColor(color: FillColor): string {
	if (color === 'transparent') return 'transparent';
	if (color === 'white') return wacToken('color.neutral.light0');
	if (color === 'black') return wacToken('color.primary.black');
	if (color.includes('secondary')) {
		return wacToken(`color.secondary.${color.replace('secondary', '')}` as keyof typeof wacToken);
	}
	const colorType = color.includes('neutral') ? 'neutral' : 'extended';
	const colorValue = color.includes('neutral') ? color.replace('neutral', 'light') : color;
	return wacToken(`color.${colorType}.${colorValue}` as keyof typeof wacToken);
}

export function pctToViewBox(pct: number): number {
	return (pct / 100) * VIEWBOX_SCALE;
}

export function pctToViewBoxY(pct: number, viewBoxHeight: number): number {
	return (pct / 100) * viewBoxHeight;
}

export function clientPointToSpiderWebPercent(
	svg: SVGSVGElement,
	clientX: number,
	clientY: number,
	viewBoxHeight: number,
): SpiderWebCanvasClickPct {
	if (viewBoxHeight <= 0) {
		return { x: 0, y: 0 };
	}
	const pt = svg.createSVGPoint();
	pt.x = clientX;
	pt.y = clientY;
	const ctm = svg.getScreenCTM();
	if (!ctm) {
		return { x: 0, y: 0 };
	}
	const p = pt.matrixTransform(ctm.inverse());
	const x = (p.x / VIEWBOX_SCALE) * 100;
	const y = (p.y / viewBoxHeight) * 100;
	return {
		x: Math.round(x * 10) / 10,
		y: Math.round(y * 10) / 10,
	};
}

function euclidean(a: WebNodeConfig, b: WebNodeConfig): number {
	const dx = a.x - b.x;
	const dy = a.y - b.y;
	return Math.sqrt(dx * dx + dy * dy);
}

function connectorKey(a: string, b: string): string {
	return a < b ? `${a}::${b}` : `${b}::${a}`;
}

export function autoConnectNodes(
	nodes: WebNodeConfig[],
	maxNeighbors: number,
	explicitConnectors?: WebConnectorConfig[],
): WebConnectorConfig[] {
	const seen = new Set<string>();
	const connectionCount = new Map<string, number>();
	const result: WebConnectorConfig[] = [];

	if (explicitConnectors) {
		for (const connector of explicitConnectors) {
			const key = connectorKey(connector.from, connector.to);
			if (!seen.has(key)) {
				seen.add(key);
				result.push(connector);
				connectionCount.set(connector.from, (connectionCount.get(connector.from) ?? 0) + 1);
				connectionCount.set(connector.to, (connectionCount.get(connector.to) ?? 0) + 1);
			}
		}
	}

	for (const node of nodes) {
		const currentCount = connectionCount.get(node.id) ?? 0;
		if (currentCount >= maxNeighbors) continue;

		const others = nodes
			.filter((n) => n.id !== node.id)
			.map((n) => ({ node: n, dist: euclidean(node, n) }))
			.sort((a, b) => a.dist - b.dist);

		for (const { node: neighbor } of others) {
			if ((connectionCount.get(node.id) ?? 0) >= maxNeighbors) break;

			const key = connectorKey(node.id, neighbor.id);
			if (seen.has(key)) continue;

			seen.add(key);
			result.push({ from: node.id, to: neighbor.id });
			connectionCount.set(node.id, (connectionCount.get(node.id) ?? 0) + 1);
			connectionCount.set(neighbor.id, (connectionCount.get(neighbor.id) ?? 0) + 1);
		}
	}

	return result;
}

const SHAPE_CYCLE: ConnectorShape[] = ['arced', 'straight', 'curvy', 'orthogonal'];

export function resolveConnectors(
	connectorConfigs: WebConnectorConfig[],
	nodes: WebNodeConfig[],
	globalConnectorShape: ConnectorShape,
	globalConnectorType: ConnectorType,
): ResolvedConnector[] {
	const nodeMap = new Map(nodes.map((n) => [n.id, n]));

	return connectorConfigs
		.map((cfg, index) => {
			const from = nodeMap.get(cfg.from);
			const to = nodeMap.get(cfg.to);
			if (!from || !to) return null;

			let shape = cfg.connectorShape ?? globalConnectorShape;
			if (shape === 'mixed') {
				shape = SHAPE_CYCLE[index % SHAPE_CYCLE.length] ?? 'arced';
			}

			const type = cfg.connectorType ?? globalConnectorType;

			return {
				from,
				to,
				connectorShape: shape,
				connectorType: type,
				index,
				connectorBias: cfg.connectorBias,
				isAnimated: cfg.isAnimated,
				animationEffect: cfg.animationEffect,
				color: cfg.color,
				animationColor: cfg.animationColor,
			} as ResolvedConnector;
		})
		.filter((e): e is ResolvedConnector => e !== null);
}

export type NodeCenterPct = {
	x: number;
	y: number;
	opacity?: number;
	size?: number;
	width?: number;
	height?: number;
};

function resolveBendSign(connector: ResolvedConnector): 1 | -1 {
	if (connector.connectorBias === 'positive') return 1;
	if (connector.connectorBias === 'negative') return -1;
	return connector.index % 2 === 0 ? 1 : -1;
}

/* true = move horizontally first, false = vertical first */
function resolveOrthogonalHorizontalFirst(connector: ResolvedConnector): boolean {
	if (connector.connectorBias === 'positive') return true;
	if (connector.connectorBias === 'negative') return false;
	return connector.index % 2 === 0;
}

// eslint-disable-next-line complexity
export function generatePathD(
	connector: ResolvedConnector,
	viewBoxHeight: number = VIEWBOX_SCALE,
	fromPct?: NodeCenterPct,
	toPct?: NodeCenterPct,
): string {
	const fx = fromPct?.x ?? connector.from.x;
	const fy = fromPct?.y ?? connector.from.y;
	const tx = toPct?.x ?? connector.to.x;
	const ty = toPct?.y ?? connector.to.y;
	const x1 = pctToViewBox(fx);
	const y1 = pctToViewBoxY(fy, viewBoxHeight);
	const x2 = pctToViewBox(tx);
	const y2 = pctToViewBoxY(ty, viewBoxHeight);

	if (connector.connectorShape === 'straight') {
		return `M ${x1} ${y1} L ${x2} ${y2}`;
	}

	if (connector.connectorShape === 'orthogonal') {
		if (resolveOrthogonalHorizontalFirst(connector)) {
			return `M ${x1} ${y1} L ${x2} ${y1} L ${x2} ${y2}`;
		}
		return `M ${x1} ${y1} L ${x1} ${y2} L ${x2} ${y2}`;
	}

	const dx = x2 - x1;
	const dy = y2 - y1;
	const dist = Math.sqrt(dx * dx + dy * dy);

	if (dist === 0) return `M ${x1} ${y1} L ${x2} ${y2}`;

	const nx = -dy / dist;
	const ny = dx / dist;

	if (connector.connectorShape === 'arced') {
		const mx = (x1 + x2) / 2;
		const my = (y1 + y2) / 2;
		const offset = dist * 0.25;
		const direction = resolveBendSign(connector);

		const qx = mx + nx * offset * direction;
		const qy = my + ny * offset * direction;

		return `M ${x1} ${y1} Q ${qx} ${qy} ${x2} ${y2}`;
	}

	if (connector.connectorShape === 'curvy') {
		const offset = dist * 0.35;
		const direction = resolveBendSign(connector);

		const cx1 = x1 + dx * 0.33 + nx * offset * direction;
		const cy1 = y1 + dy * 0.33 + ny * offset * direction;
		const cx2 = x1 + dx * 0.66 - nx * offset * direction;
		const cy2 = y1 + dy * 0.66 - ny * offset * direction;

		return `M ${x1} ${y1} C ${cx1} ${cy1} ${cx2} ${cy2} ${x2} ${y2}`;
	}

	return `M ${x1} ${y1} L ${x2} ${y2}`;
}

export function getHexagonPoints(cx: number, cy: number, r: number): string {
	const points: string[] = [];
	for (let i = 0; i < 6; i++) {
		const angle = (Math.PI / 3) * i - Math.PI / 2;
		points.push(`${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`);
	}
	return points.join(' ');
}

export function getTrianglePoints(cx: number, cy: number, r: number): string {
	const top = `${cx},${cy - r}`;
	const bottomLeft = `${cx - r * 0.866},${cy + r * 0.5}`;
	const bottomRight = `${cx + r * 0.866},${cy + r * 0.5}`;
	return `${top} ${bottomLeft} ${bottomRight}`;
}

export function estimatePathLength(
	connector: ResolvedConnector,
	viewBoxHeight: number = VIEWBOX_SCALE,
	fromPct?: NodeCenterPct,
	toPct?: NodeCenterPct,
): number {
	const fx = fromPct?.x ?? connector.from.x;
	const fy = fromPct?.y ?? connector.from.y;
	const tx = toPct?.x ?? connector.to.x;
	const ty = toPct?.y ?? connector.to.y;
	const x1 = pctToViewBox(fx);
	const y1 = pctToViewBoxY(fy, viewBoxHeight);
	const x2 = pctToViewBox(tx);
	const y2 = pctToViewBoxY(ty, viewBoxHeight);
	const dx = x2 - x1;
	const dy = y2 - y1;
	const straightDist = Math.sqrt(dx * dx + dy * dy);

	if (connector.connectorShape === 'arced') {
		return straightDist * 1.2;
	}
	if (connector.connectorShape === 'curvy') {
		return straightDist * 1.5;
	}
	if (connector.connectorShape === 'orthogonal') {
		return Math.abs(dx) + Math.abs(dy);
	}
	return straightDist;
}

const DEFAULT_NODE_SIZE = 100;
const PILL_ASPECT = 2.8;
const PILL_HORIZONTAL_PADDING = 0.8;
const RECTANGLE_HORIZONTAL_PADDING = 0.8;
const MONOSPACE_CHAR_WIDTH_RATIO = 0.6;

/** Global tuning for `insideLeading` / `outsideLeading` (see `WebNode`). */
export const LEADING_ICON_INSIDE_GAP_RATIO = 0.3;
export const LEADING_INSIDE_EXTRA_WIDTH_RATIO = 0.0005;
export const LEADING_ICON_OUTSIDE_GAP_RATIO = 0.14;

/**
 * Inside-leading icon square side ≈ `labelFontSize * this`, capped by the
 * pill/row height so it stays square and roughly text-high.
 */
export const LEADING_INSIDE_ICON_EM_SCALE = 1.15;

export function leadingInsideIconSidePx(node: WebNodeConfig, maxBodySide: number): number {
	const size = node.size ?? DEFAULT_NODE_SIZE;
	const fontFamily = node.fontFamily ?? 'monospace';
	const labelLayoutFontSize = fontFamily === 'monospace' ? size * 0.3 : size * 0.4;
	const desired = labelLayoutFontSize * LEADING_INSIDE_ICON_EM_SCALE;
	return Math.min(Math.max(4, desired), maxBodySide);
}

export function effectiveLeadingImagePlacement(node: WebNodeConfig): NodeImagePlacement {
	const raw = node.imagePlacement ?? 'background';
	if (
		raw === 'background' ||
		!node.bgImage ||
		!node.text ||
		(node.shape !== 'pill' && node.shape !== 'rectangle')
	) {
		return 'background';
	}
	return raw;
}

export type NodeOverlapLayout = {
	cx: number;
	cy: number;
	r: number;
	size: number;
	pillWidth: number;
	rectangleWidth: number;
	rectangleHeight: number;
	rectangleBorderRadius?: number;
	rotate: number;
	shape: WebNodeConfig['shape'];
};

export type NodeBoxDimensions = {
	size: number;
	boxWidth: number;
	boxHeight: number;
};

const RECTANGLE_RX_DEFAULT_DIVISOR = 4;

export function getRectangleBorderRadius(
	node: WebNodeConfig,
	rectangleWidth: number,
	rectangleHeight: number,
): number {
	if (node.shape !== 'rectangle') {
		return 0;
	}
	const minSide = Math.min(rectangleWidth, rectangleHeight);
	if (minSide <= 0) {
		return 0;
	}
	const maxRx = minSide / 2;
	const defaultRx = minSide / RECTANGLE_RX_DEFAULT_DIVISOR;
	const raw = node.borderRadius ?? defaultRx;
	return Math.min(Math.max(0, raw), maxRx);
}

/* eslint-disable-next-line complexity -- mirrors WebNode pill/rectangle leading layout */
function computeNodeBoxWidths(node: WebNodeConfig): {
	size: number;
	pillWidth: number;
	rectangleWidth: number;
	rectangleHeight: number;
} {
	const size = node.size ?? DEFAULT_NODE_SIZE;
	const fontFamily = node.fontFamily ?? 'monospace';
	const labelLayoutFontSize = fontFamily === 'monospace' ? size * 0.3 : size * 0.4;
	const placement = effectiveLeadingImagePlacement(node);

	const textStretchW =
		node.text && (node.shape === 'pill' || node.shape === 'rectangle')
			? node.text.length * labelLayoutFontSize * MONOSPACE_CHAR_WIDTH_RATIO
			: 0;

	const pillBodyTextWidth =
		node.shape === 'pill' && node.text
			? textStretchW + size * PILL_HORIZONTAL_PADDING
			: size * PILL_ASPECT;

	let pillWidth = pillBodyTextWidth;
	if (node.shape === 'pill' && node.text && node.bgImage && placement === 'insideLeading') {
		const iconSide = leadingInsideIconSidePx(node, size);
		pillWidth =
			pillBodyTextWidth +
			iconSide +
			size * LEADING_ICON_INSIDE_GAP_RATIO +
			size * LEADING_INSIDE_EXTRA_WIDTH_RATIO;
	}

	const rectangleHeight = node.height ?? size;

	const rectangleBodyTextWidth =
		node.shape === 'rectangle' && node.text
			? textStretchW + size * RECTANGLE_HORIZONTAL_PADDING
			: size;

	let rectangleWidth =
		node.width ?? (node.shape === 'rectangle' && node.text ? rectangleBodyTextWidth : size);

	if (
		node.width === undefined &&
		node.shape === 'rectangle' &&
		node.text &&
		node.bgImage &&
		placement === 'insideLeading'
	) {
		const iconSide = leadingInsideIconSidePx(node, rectangleHeight);
		rectangleWidth =
			rectangleBodyTextWidth +
			iconSide +
			rectangleHeight * LEADING_ICON_INSIDE_GAP_RATIO +
			Math.max(size, rectangleHeight) * LEADING_INSIDE_EXTRA_WIDTH_RATIO;
	}

	return { size, pillWidth, rectangleWidth, rectangleHeight };
}

export function getNodeLayoutDimensions(node: WebNodeConfig): NodeBoxDimensions {
	const { size, pillWidth, rectangleWidth, rectangleHeight } = computeNodeBoxWidths(node);

	switch (node.shape) {
		case 'pill':
			return { size, boxWidth: pillWidth, boxHeight: size };
		case 'rectangle':
			return {
				size,
				boxWidth: rectangleWidth,
				boxHeight: rectangleHeight,
			};
		default:
			return { size, boxWidth: size, boxHeight: size };
	}
}

/* eslint-disable complexity -- box vs shape branches mirror WebNode layout */
export function getNodeOverlapLayout(
	node: WebNodeConfig,
	viewBoxHeight: number,
	centerPct?: NodeCenterPct,
): NodeOverlapLayout {
	const size = centerPct?.size !== undefined ? centerPct.size : (node.size ?? DEFAULT_NODE_SIZE);
	const nodeForDims: WebNodeConfig = { ...node, size };
	const dims = getNodeLayoutDimensions(nodeForDims);
	const boxW = centerPct?.width !== undefined ? centerPct.width : dims.boxWidth;
	const boxH = centerPct?.height !== undefined ? centerPct.height : dims.boxHeight;
	const r = size / 2;
	const pillWidth = node.shape === 'pill' ? boxW : size * PILL_ASPECT;
	const rectangleWidth = node.shape === 'rectangle' ? boxW : dims.boxWidth;
	const rectangleHeight = node.shape === 'rectangle' ? boxH : dims.boxHeight;
	const px = centerPct?.x ?? node.x;
	const py = centerPct?.y ?? node.y;
	const rectangleBorderRadius =
		node.shape === 'rectangle'
			? getRectangleBorderRadius(node, rectangleWidth, rectangleHeight)
			: undefined;
	return {
		cx: pctToViewBox(px),
		cy: pctToViewBoxY(py, viewBoxHeight),
		r,
		size,
		pillWidth,
		rectangleWidth,
		rectangleHeight,
		rectangleBorderRadius,
		rotate: node.rotate ?? 0,
		shape: node.shape,
	};
}
/* eslint-enable complexity */

export { VIEWBOX_SCALE };
